import {
  getAaChange,
  getCopyNumberType,
  getCopyNumberValue,
  getEffect,
  getEvidenceLevelValue,
  getExpressionValueAndUnit,
  getGene,
  getNcChange,
  getNotes,
  getRecommendation,
  getTrialId,
  getVariantType,
  isManualFinding,
} from './observationUtils';
import { VariantType } from './types';
import { EVIDENCE_LEVEL_COMPONENT_CODING } from './constants';

const getSurveyResponse = (
  observation: fhir.Observation,
  getValueString: () => string,
): fhir.Extension => {
  const valueString = getValueString();

  return {
    url: 'http://lifeomic.com/fhir/survey-response-selected-variant',
    ...(valueString ? { valueString } : {}),
    id: observation.id,
  } as fhir.Extension;
};

const getShortVariantSurveyResponse = (
  observation: fhir.Observation,
  formatMessageFunc: (label: string) => string,
): fhir.Extension | undefined =>
  getSurveyResponse(observation, () =>
    [getGene(observation), getAaChange(observation), getNcChange(observation)]
      .filter(Boolean)
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      .map(formatMessageFunc)
      .join(':'),
  );

const getStructuralSurveyResponse = (
  observation: fhir.Observation,
  formatMessageFunc: (label: string) => string,
): fhir.Extension | undefined =>
  getSurveyResponse(observation, () =>
    [getGene(observation), getEffect(observation)]
      .filter(Boolean)
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      .map(formatMessageFunc)
      .join(':'),
  );

const getCopyNumberSurveyResponse = (
  observation: fhir.Observation,
  formatMessageFunc: (label: string) => string,
) =>
  getSurveyResponse(observation, () =>
    [
      getGene(observation),
      getCopyNumberType(observation),
      getCopyNumberValue(observation)
        ? `CN=${getCopyNumberValue(observation)}`
        : undefined,
    ]
      .filter(Boolean)
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      .map(formatMessageFunc)
      .join(':'),
  );

const getExpressionSurveyResponse = (
  observation: fhir.Observation,
  formatMessageFunc: (label: string) => string,
): fhir.Extension | undefined =>
  getSurveyResponse(observation, () => {
    const { value, unit } = getExpressionValueAndUnit(observation) || {};
    const expression = value ? `${value} ${unit}` : undefined;

    return (
      [getGene(observation), expression]
        .filter(Boolean)
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        .map(formatMessageFunc)
        .join(':')
    );
  });

const getManualFindingResponse = (
  observation: fhir.Observation,
): fhir.Extension | undefined =>
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  getSurveyResponse(observation, () => {
    const component = observation.component?.find(
      ({ code }) =>
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        code.coding[0].system ===
          'http://hl7.org/fhir/uv/genomics-reporting/CodeSystem/tbd-codes-cs' &&
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        code.coding[0].code === 'conclusion-string',
    );
    return component?.valueString;
  });

export const getVariantSurveyResponse = (
  observation: fhir.Observation,
  formatMessageFunc: (label: string) => string,
): fhir.Extension | undefined => {
  const mapping: Record<
    string,
    (
      observation: fhir.Observation,
      formatMessageFunc: (label: string) => string,
    ) => fhir.Extension
  > = {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    shortVariant: getShortVariantSurveyResponse,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    structuralVariant: getStructuralSurveyResponse,
    copyNumberVariant: getCopyNumberSurveyResponse,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    expressionVariant: getExpressionSurveyResponse,
  };

  const type = getVariantType(observation) as VariantType;
  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const mapper: (
    observation: fhir.Observation,
    formatMessageFunc: (label: string) => string,
  ) => fhir.Extension = isManualFinding(observation)
    ? getManualFindingResponse
    : mapping[type];
  if (!mapper) {
    return undefined;
  }
  const result = mapper(observation, formatMessageFunc);
  const recommendation = getRecommendation(observation);
  if (recommendation) {
    result['extension'] = [
      {
        url: 'http://lifeomic.com/fhir/survey-response-recommendations',
        valueString: recommendation,
      },
    ];
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const evidenceLevel: fhir.Coding = getEvidenceLevelValue(
    observation,
    EVIDENCE_LEVEL_COMPONENT_CODING,
  );
  if (evidenceLevel) {
    if (!result['extension']) {
      result['extension'] = [];
    }
    result['extension'].push({
      url: 'http://lifeomic.com/fhir/survey-response-evidence-level',
      valueCoding: evidenceLevel,
    });
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const notes: string = getNotes(observation);
  if (notes) {
    if (!result['extension']) {
      result['extension'] = [];
    }
    result['extension'].push({
      url: 'http://lifeomic.com/fhir/survey-response-genomics-artifact',
      valueString: notes,
    });
  }

  const trialId = getTrialId(observation);
  if (trialId) {
    if (!result['extension']) {
      result['extension'] = [];
    }
    result['extension'].push({
      url: 'http://lifeomic.com/fhir/survey-response-trial-id',
      valueString: trialId,
    });
  }

  return result;
};
